import { useQueryClient } from '@tanstack/react-query'
import { api } from '../http'
import { buildJsonApiParams } from '../jsonApi'
import { queryKeys } from '../queryKeys'
import {
    useApiQuery,
    useApiPagedInfiniteQuery,
    useApiMutation,
} from './_base'
import { useActiveCompanyId } from 'contexts/ActiveCompanyContext'
import { invalidateCompanyScopedQueries } from '../queryClient'

/** GET /companies */
export function useCompanyList(params = {}, options = {}) {
    const apiParams = buildJsonApiParams(params)
    return useApiQuery({
        queryKey: queryKeys.companies.list(apiParams),
        url: '/companies',
        params: apiParams,
        options,
    })
}

export function useCompaniesInfinite(params = {}, options = {}) {
    return useApiPagedInfiniteQuery({
        queryKey: queryKeys.companies.infinite(params),
        url: '/companies',
        buildPageParams: (page) =>
            buildJsonApiParams({
                ...params,
                page: { ...(params.page || {}), number: page },
            }),
        options,
    })
}

/** GET /companies/{id} */
export function useCompany(id, params = {}, options = {}) {
    const apiParams = buildJsonApiParams(params)
    // Guard against a non-numeric id (e.g. an object stringified to
    // "[object Object]") ever reaching the URL. Fetch only with a real id.
    const validId = /^\d+$/.test(String(id ?? '')) ? id : undefined
    return useApiQuery({
        queryKey: queryKeys.companies.detail(validId, apiParams),
        url: `/companies/${validId}`,
        params: apiParams,
        options: { enabled: !!validId, ...options },
    })
}

/** Convenience: the currently-active company, fully loaded. */
export function useActiveCompany(params = {}, options = {}) {
    const id = useActiveCompanyId()
    return useCompany(id, params, options)
}

/** POST /companies */
export function useCreateCompany() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['companies', 'create'],
        mutationFn: (payload) => api.post('/companies', {}, payload),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: queryKeys.companies.all })
            qc.invalidateQueries({ queryKey: queryKeys.session.me })
        },
    })
}

/** PUT /companies/{id} */
export function useUpdateCompany() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['companies', 'update'],
        mutationFn: ({ id, ...payload }) =>
            api.put(`/companies/${id}`, {}, payload),
        onSuccess: (_d, { id }) => {
            qc.invalidateQueries({ queryKey: queryKeys.companies.detail(id) })
            qc.invalidateQueries({ queryKey: queryKeys.companies.lists() })
            qc.invalidateQueries({ queryKey: queryKeys.session.me })
        },
    })
}

/** DELETE /companies/{id} */
export function useDeleteCompany() {
    const qc = useQueryClient()
    return useApiMutation({
        mutationKey: ['companies', 'delete'],
        mutationFn: (id) => api.delete(`/companies/${id}`),
        onSuccess: () => {
            invalidateCompanyScopedQueries()
            qc.invalidateQueries({ queryKey: queryKeys.companies.all })
            qc.invalidateQueries({ queryKey: queryKeys.session.me })
        },
    })
}

/**
 * Users that belong to a company (uses the /users endpoint with a filter so we
 * benefit from the existing eager-loading + permissions).
 */
export function useCompanyUsers(companyId, params = {}, options = {}) {
    const apiParams = buildJsonApiParams({
        ...params,
        filter: { ...(params.filter || {}), 'companies.id': companyId },
    })
    return useApiQuery({
        queryKey: queryKeys.users.list({ companyId, ...apiParams }),
        url: '/users',
        params: apiParams,
        options: { enabled: !!companyId, ...options },
    })
}
